import { NetworkStatus } from "@apollo/client";
import isEqual from "lodash-es/isEqual";
import { useCallback, useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import useErrorHandlerSourceCode from "components/Forms/SourceCode/useErrorHandlerSourceCode";
import MissingDataBanner from "components/MissingDataBanner";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import FormLoading from "components/form/components/loading";
import { TerraformWorkflowToolOptions } from "constants/terraform_workflow_tools";
import { WORKER_POOL_SHARED_VALUE } from "constants/worker_pool";
import Box from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import Input from "ds/components/Input";
import Link from "ds/components/Link";
import Select from "ds/components/Select";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import { useFormValidations } from "hooks/useFormValidations";
import useTypedContext from "hooks/useTypedContext";
import { useWorkerPoolOptions } from "shared/WorkerPool/useWorkerPoolOptions";
import { TerraformWorkflowTool } from "types/generated";
import { withoutPublicWorkerPool } from "utils/distribution";
import { getDocsUrl } from "utils/getDocsUrl";

import NewModuleFooter from "../Footer";
import { ModuleFormContext } from "../context";
import { ModuleBehaviorFormFields, ModuleCreationWizardStep } from "../types";
import { useNewModuleAnalyticsSegmentEvent } from "../useNewModuleAnalyticsSegmentEvent";
import { getSwitchToggleAnalyticsProps, getTooltipAnalyticsProps } from "../utils";
import Documentation from "./Documentation";

const noPublicWorkerPool = withoutPublicWorkerPool();

const getWorkerPoolInitialData = (workerPool: string) => {
  if (noPublicWorkerPool && workerPool === WORKER_POOL_SHARED_VALUE) {
    return "";
  }

  return workerPool;
};

const NewModuleBehavior = () => {
  const {
    currentStep,
    updateStepData,
    formData,
    setInternalFormData,
    internalData,
    setAccountOrAuthErrorContent,
  } = useTypedContext(ModuleFormContext);
  const stepData = formData[ModuleCreationWizardStep.Behavior];

  const { workerPoolsOptions, loading, hasData, error, refetch, networkStatus } =
    useWorkerPoolOptions(formData[ModuleCreationWizardStep.Vcs].space);

  useErrorHandlerSourceCode(setAccountOrAuthErrorContent, error);

  const initialWorkerPool = getWorkerPoolInitialData(stepData.workerPool);

  const trackSegmentEvent = useNewModuleAnalyticsSegmentEvent();

  const builderForm = useForm<ModuleBehaviorFormFields>({
    defaultValues: {
      ...stepData,
      workerPool: initialWorkerPool,
    },
    mode: "onChange",
  });

  const { control, setValue, watch, trigger, register } = builderForm;
  const runFormValidations = useFormValidations(builderForm);

  const newStepData = watch();

  const processStepData = () => {
    const workerPoolOption = workerPoolsOptions.find(
      ({ value }) => value === newStepData.workerPool
    );
    setInternalFormData({ ...internalData, workerPoolLabel: workerPoolOption?.label });

    return updateStepData(currentStep, newStepData);
  };

  const isDataChanged = !isEqual(newStepData, stepData);

  const selectedWorkerPool = watch("workerPool");

  useEffect(() => {
    if (!selectedWorkerPool) {
      if (noPublicWorkerPool && workerPoolsOptions?.length) {
        setValue("workerPool", workerPoolsOptions[0].value, { shouldValidate: true });
      }
    } else if (hasData || (!loading && !hasData)) {
      // validate worker pool shared via link
      const isNotAvailable = !workerPoolsOptions.find(({ value }) => {
        return value === selectedWorkerPool;
      });
      if (isNotAvailable) {
        setValue("workerPool", "");
      }
    }
  }, [workerPoolsOptions, setValue, selectedWorkerPool, hasData, loading]);

  const handleWorkerPoolChange = (workerPool: string) => {
    setValue("workerPool", workerPool, { shouldValidate: true });
  };

  const handleWorkflowToolChange = useCallback(
    (value: TerraformWorkflowTool) => {
      setValue("workflowTool", value);

      trigger();

      trackSegmentEvent("Workflow tool", { tool: value, provider: "Terraform / OpenTofu" });
    },
    [setValue, trigger, trackSegmentEvent]
  );

  if (loading && networkStatus !== NetworkStatus.refetch) {
    return <FormLoading />;
  }

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title={<>Define behavior {noPublicWorkerPool && "(optional)"}</>}
          description="Define additional module settings"
        />
        <Box margin="0 0 large">
          <FormProvider {...builderForm}>
            <Box direction="column" gap="large">
              <Controller
                name="workerPool"
                rules={{
                  validate: (value) => {
                    if (workerPoolsOptions.length === 0) {
                      return "No worker pools available. Please create a worker pool first.";
                    }
                    if (!value) {
                      return "Worker pool is required";
                    }
                    return true;
                  },
                }}
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Select
                      error={fieldState.error?.message}
                      label="Worker pool"
                      {...getTooltipAnalyticsProps("Behavior", "Worker pool")}
                      tooltipInfo={
                        <>
                          <TooltipModalTitle>Worker pool</TooltipModalTitle>
                          <TooltipModalBody align="start">
                            <Typography tag="p" variant="p-body3">
                              By using a private worker pool you make sure that you have full
                              control over your infrastructure changes, and can even access
                              resources that would otherwise not be reachable from the public
                              internet.
                            </Typography>
                            <Typography tag="p" variant="p-body3">
                              If you don't have a private worker pool, you can create one in the{" "}
                              <Link href="/worker-pools" inheritFont>
                                Worker Pools screen
                              </Link>
                              .
                            </Typography>
                            <ReadMoreDocsLink docsUrl={getDocsUrl("/concepts/worker-pools")} />
                          </TooltipModalBody>
                        </>
                      }
                      isDisabled={workerPoolsOptions.length <= 1}
                      value={field.value}
                      items={workerPoolsOptions}
                      placeholder="Select worker pool"
                      onChange={handleWorkerPoolChange}
                    />
                    {!hasData && (
                      <Box direction="column" margin="large 0 0 0">
                        <MissingDataBanner
                          text="Couldn't load your private worker pools, please refresh or come back later"
                          refreshHandler={refetch}
                          refreshLoading={loading && networkStatus === NetworkStatus.refetch}
                        />
                      </Box>
                    )}
                  </>
                )}
              />
              <Controller
                name="workflowTool"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    error={fieldState.error?.message}
                    label="Workflow tool"
                    {...getTooltipAnalyticsProps("Vendor", "Workflow tool", {
                      provider: "Terraform / OpenTofu",
                    })}
                    tooltipInfo={
                      <>
                        <TooltipModalTitle>Workflow tool</TooltipModalTitle>
                        <TooltipModalBody align="start">
                          The tool used to execute the workflow commands. This can be an open source
                          (FOSS) version of OpenTofu, Terraform or a custom tool.
                        </TooltipModalBody>
                      </>
                    }
                    value={field.value as TerraformWorkflowTool}
                    items={TerraformWorkflowToolOptions}
                    onChange={handleWorkflowToolChange}
                  />
                )}
              />
              <FormField
                label="Runner image"
                isOptional
                noMargin
                tooltipInfo={
                  <>
                    <TooltipModalTitle>Runner image</TooltipModalTitle>
                    <TooltipModalBody align="start">
                      <Typography tag="p" variant="p-body3">
                        Optional runner image used to process the module metadata during parsing
                        Run.
                      </Typography>
                      <ReadMoreDocsLink
                        docsUrl={getDocsUrl("/concepts/stack/stack-settings#runner-image")}
                      />
                    </TooltipModalBody>
                  </>
                }
                tooltipInfoVariant="modal"
              >
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Runner image"
                    {...register("runnerImage", {
                      setValueAs: (value: string) => value.trim(),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <Controller
                name="administrative"
                control={control}
                render={({ field }) => (
                  <FormToggleField
                    variant="switch"
                    onChange={field.onChange}
                    checked={field.value}
                    title="Administrative"
                    description="Module will receive a runtime environment variable giving administrative access to other stacks within the same account"
                    {...getSwitchToggleAnalyticsProps("Behavior", "Administrative", true)}
                    tooltipInfo={
                      <>
                        <TooltipModalTitle>Administrative module</TooltipModalTitle>
                        <TooltipModalBody align="start">
                          <Typography tag="p" variant="p-body3">
                            Administrative modules can create, update and destroy Spacelift
                            resources.
                          </Typography>
                          <ReadMoreDocsLink
                            docsUrl={getDocsUrl("/concepts/stack/stack-settings#administrative")}
                          />
                        </TooltipModalBody>
                      </>
                    }
                  />
                )}
              />

              <Controller
                name="localPreviewEnabled"
                control={control}
                render={({ field }) => (
                  <FormToggleField
                    variant="switch"
                    onChange={field.onChange}
                    checked={field.value}
                    title="Enable local preview"
                    description="Run local previews using spacectl."
                    {...getSwitchToggleAnalyticsProps("Behavior", "Enable local preview", true)}
                    tooltipInfo={
                      <>
                        <TooltipModalTitle>Local preview</TooltipModalTitle>
                        <TooltipModalBody align="start">
                          <Typography tag="p" variant="p-body3">
                            When set to true, proposed runs can be created based on user-uploaded
                            local workspaces. This way you can preview how your code changes will
                            execute without creating a commit.
                          </Typography>
                          <ReadMoreDocsLink
                            docsUrl={getDocsUrl(
                              "/concepts/stack/stack-settings#enable-local-preview"
                            )}
                          />
                        </TooltipModalBody>
                      </>
                    }
                  />
                )}
              />
              <Controller
                name="protectFromDeletion"
                control={control}
                render={({ field }) => (
                  <FormToggleField
                    variant="switch"
                    onChange={field.onChange}
                    checked={field.value}
                    title="Protect from deletion (recommended)"
                    description="Manage deletion protection"
                    {...getSwitchToggleAnalyticsProps("Behavior", "Protect from deletion", true)}
                    tooltipInfo={
                      <>
                        <TooltipModalTitle>Protect from deletion</TooltipModalTitle>
                        <TooltipModalBody align="start">
                          <Typography tag="p" variant="p-body3">
                            Deletion protection helps protect your modules from accidental deletion.
                            When enabled, any attempts to delete your module will fail.
                          </Typography>
                        </TooltipModalBody>
                      </>
                    }
                  />
                )}
              />
            </Box>
          </FormProvider>
        </Box>
      </FullScreenModalBody>
      <NewModuleFooter
        isDataChanged={isDataChanged}
        processStepData={processStepData}
        documentationLink={getDocsUrl("/vendors/terraform/module-registry#spacelift-setup")}
        documentationTitle="Define behavior"
        documentationBody={<Documentation />}
        runFormValidations={runFormValidations}
      />
    </>
  );
};

export default NewModuleBehavior;
